import React, { useState } from 'react';
import styled from 'styled-components';

import { Tooltip, Button } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined, MoreOutlined, SwapOutlined } from '@ant-design/icons';

import { SegmentationLabel } from 'models/segmentation';
import { Segmentation } from 'models/predictions';

interface SegmentationOverlayActionsProps {
  segmentations: SegmentationLabel[] | Segmentation[];
  visibleSegmentations: Set<string>;
  onToggleSegmentation: (icid: string, isVisible: boolean) => void;
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  onToggleDifference?: () => void;
  isDifferenceShown?: boolean;
  highlightedSegmentationClass?: string | null;
}

const OverlayContainer = styled.div`
  position: relative;
  width: fit-content;
  height: fit-content;
`;

const TailIndicator = styled.div<{ $shouldShow: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  width: 2.5rem;
  height: 2.5rem;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  z-index: 3;
  opacity: ${props => (props.$shouldShow ? 0 : 1)};
  visibility: ${props => (props.$shouldShow ? 'hidden' : 'visible')};
  transform: ${props => (props.$shouldShow ? 'scale(0.8)' : 'scale(1)')};

  &:hover {
    background: rgba(0, 0, 0, 0.8);
    transform: ${props => (props.$shouldShow ? 'scale(0.8)' : 'scale(1.1)')};
  }
`;

const SwapIndicator = styled.div<{ $active?: boolean }>`
  position: absolute;
  top: 2.9rem;
  right: 0;
  width: 2rem;
  height: 2rem;
  background: rgba(0, 0, 0, 0.65);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  z-index: 4;
  color: ${props => (props.$active ? '#1890ff' : '#fff')};
  transition: all 0.18s ease;

  &:hover {
    transform: scale(1.05);
    background: rgba(0, 0, 0, 0.78);
  }
`;

/* Diff button moved to the comparator so it can be placed on the card edge */

const CustomActionPanel = styled.div<{ $shouldShow: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  height: 2.5rem;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(8px);
  border-radius: 1.25rem;
  display: flex;
  align-items: center;
  padding: 0 0.5rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
  z-index: 2;
  opacity: ${props => (props.$shouldShow ? 1 : 0)};
  transform: ${props => (props.$shouldShow ? 'scale(1)' : 'scale(0.8)')};
  transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: ${props => (props.$shouldShow ? 'auto' : 'none')};

  @media (prefers-reduced-motion: reduce) {
    transition-duration: 0.01ms;
  }
`;

const CustomActionButton = styled(Button)<{
  $isVisible?: boolean;
}>`
  width: 1.75rem;
  height: 1.75rem;
  min-width: 1.75rem;
  min-height: 1.75rem;
  border-radius: 4px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 0.2s ease;
  margin: 0 1px;

  &.ant-btn-text {
    color: ${props => (props.$isVisible ? '#1890ff' : 'rgba(255, 255, 255, 0.8)')};
    background-color: ${props => (props.$isVisible ? 'rgba(24, 144, 255, 0.2)' : 'transparent')};
  }

  &:hover.ant-btn-text {
    background-color: ${props =>
      props.$isVisible ? 'rgba(24, 144, 255, 0.3)' : 'rgba(255, 255, 255, 0.2)'};
    color: ${props => (props.$isVisible ? '#1890ff' : '#fff')};
    transform: scale(1.1);
  }

  &:focus.ant-btn-text {
    background-color: ${props =>
      props.$isVisible ? 'rgba(24, 144, 255, 0.3)' : 'rgba(255, 255, 255, 0.2)'};
  }
`;

const SegmentationOverlayActions: React.FC<SegmentationOverlayActionsProps> = props => {
  const {
    segmentations,
    visibleSegmentations,
    onToggleSegmentation,
    position = 'top-left',
    onToggleDifference,
    isDifferenceShown,
  } = props;
  const [isHovered, setIsHovered] = useState(false);

  if (segmentations.length === 0) {
    return null;
  }

  const getPositionStyles = () => {
    const baseStyles = {
      position: 'absolute' as const,
      zIndex: 20,
    };

    switch (position) {
      case 'top-left':
        return { ...baseStyles, top: '8px', left: '8px' };
      case 'top-right':
        return { ...baseStyles, top: '8px', right: '8px' };
      case 'bottom-left':
        return { ...baseStyles, bottom: '8px', left: '8px' };
      case 'bottom-right':
        return { ...baseStyles, bottom: '8px', right: '8px' };
      default:
        return { ...baseStyles, top: '8px', left: '8px' };
    }
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  // Helper function to check if segmentation has datasourceName
  const getDatasourceName = (seg: SegmentationLabel | Segmentation) => {
    return 'datasourceName' in seg ? seg.datasourceName : 'Segmentation';
  };
  // Helper to get segmentation class/name in a user friendly form
  const getSegmentationClassName = (seg: SegmentationLabel | Segmentation) => {
    const raw =
      (seg as any).segmentationClassValue ??
      (seg as any).segmentationClass ??
      (seg as any).segmentation_class ??
      (seg as any).segmentationClassName ??
      null;
    if (!raw || typeof raw !== 'string') return null;
    // Title-case each word for nicer label: "left atrium" -> "Left Atrium"
    return raw
      .split(/\s+/)
      .map(w => (w.length > 0 ? w[0].toUpperCase() + w.slice(1) : w))
      .join(' ');
  };

  return (
    <OverlayContainer style={getPositionStyles()}>
      <TailIndicator
        $shouldShow={isHovered}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <MoreOutlined style={{ fontSize: 14, color: '#fff' }} />
      </TailIndicator>

      {onToggleDifference && (
        <SwapIndicator $active={Boolean(isDifferenceShown)} onClick={() => onToggleDifference()}>
          <SwapOutlined style={{ fontSize: 14 }} />
        </SwapIndicator>
      )}

      <CustomActionPanel
        $shouldShow={isHovered}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {/* Swap/diff control rendered as a circular indicator outside the action panel */}
          {segmentations.map((seg, idx) => {
            const isVisible = visibleSegmentations.has(seg.icid);
            // Prefer segmentation's own class; if missing for the first item,
            // fall back to the outer highlighted segmentation class passed by parent.
            const className =
              getSegmentationClassName(seg) ||
              (idx === 0 && (props as any).highlightedSegmentationClass
                ? String((props as any).highlightedSegmentationClass)
                    .split(/\s+/)
                    .map(w => (w.length > 0 ? w[0].toUpperCase() + w.slice(1) : w))
                    .join(' ')
                : getDatasourceName(seg));
            const verb = isVisible ? 'Hide' : 'Show';
            return (
              <Tooltip key={seg.icid} title={`${verb} ${className}`} placement="bottom">
                <CustomActionButton
                  type="text"
                  icon={React.cloneElement(isVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />, {
                    style: { fontSize: 14, color: (seg as any).segmentationColor || undefined },
                  })}
                  onClick={() => onToggleSegmentation(seg.icid, !isVisible)}
                  $isVisible={isVisible}
                />
              </Tooltip>
            );
          })}
        </div>
      </CustomActionPanel>
    </OverlayContainer>
  );
};

export default SegmentationOverlayActions;
